import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Empty, Form, Pagination, Row, Skeleton, Table, Tooltip } from 'antd';
import { TABLE_SIZE_MIDDLE } from '../../../shared/constants/crmConstants';
import { fetchGrievanceList, searchClaimList, replyGrievance } from '../../../store/actions/grievanceAction';
import '../grievance.less';
import { View_Grievance } from '../services/grievanceConstants';
import { useHistory } from 'react-router-dom';
import { base64UrlEncoded, centeredNotification } from '../../../utils/utility';
import { checkIfAnyTableSearchEnabled, getTableBasedOnConfig, hasReplyStatus } from '../../crm/inPatient/service/crmUtils';
import { RootState } from '../../../store';
import { clearSearchDetail, saveInPatientConfig, searchInPatientList, setPageIndex, setSortingPreference } from '../../../store/actions/inPatientAction';
import { ReactComponent as ReplyIcon } from '../../../assets/images/svg/forward-reply.svg';
import { ReactComponent as ReopenIcon } from '../../../assets/images/svg/Re-Open_Icon.svg';
import { COMMON_ERROR, GRIEVANCE_ACTIONS, GRIEVANCE_DETAIL_ROUTE, GUTTOR_SPACE, REOPEN, TICKET_QUERY } from '../../../shared/constants/commonConstants';
import TableConfigurator from '../../../shared/components/TableConfigurator/TableConfigurator';
import { FormInstance } from 'antd/lib/form';
import { ApplicationDetailModel } from '../../../store/models/applicationDetailModel';
import { fetchInpatientStatusList, setAllGrievance, setGrievanceStatuses } from '../../../store/actions/lookUpAction';
import { isEmpty } from 'lodash';

const GrievanceList = (): ReactElement => {
  const blankArr = Array(10).fill(0);
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    selectedInPatientDetail: { filters, sortingKey, isSearchClicked, pageIndex },
  } = useSelector((state: RootState): any => state || {});
  const appDetail = useSelector((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const { payerClaimStatuses }: any = useSelector((state: RootState): any => state.lookUp);
  const [isGrievanceLoading, setIsGrievanceLoading] = useState(false);
  const [grievanceList, setGrievanceList] = useState([] as any);
  const [claimDataCount, setClaimDataCount] = useState(-1);
  const [claimsColumns, setClaimsColumns] = useState([] as any);
  const [, setIsColumnFilter] = useState(false);
  const [viewForm] = Form.useForm();
  const payerClaimStatus = JSON.parse(localStorage.getItem('payerClaimStatuses') || '{}');
  const grievanceStatus = JSON.parse(localStorage.getItem('grievanceStatus') || '{}');

  useEffect((): any => {
    dispatch(clearSearchDetail());
    if (payerClaimStatus?.length) {
      dispatch(setGrievanceStatuses(grievanceStatus));
    }
    if (isEmpty(payerClaimStatus || payerClaimStatuses)) {
      dispatch(fetchInpatientStatusList());
    }
    dispatch(setAllGrievance([]));
  }, []);

  useEffect((): any => {
    if (checkIfAnyTableSearchEnabled(filters, sortingKey)) {
      setIsColumnFilter(true);
    } else {
      setIsColumnFilter(false);
    }
  }, [filters, sortingKey]);

  useEffect((): any => {
    setLoaderStateValue();
    setIsGrievanceLoading(true);
    dispatch(fetchGrievanceList({
      getClaimsList,
    }));
  }, [isSearchClicked]);

  useEffect((): any => {
    const filteredTable = getTableBasedOnConfig(appDetail.getGrievanceConfig(), View_Grievance);
    setClaimsColumns(
      [
        ...filteredTable,
        {
          title: 'Actions',
          dataIndex: '',
          render: (_: any, record: any): JSX.Element | null => {

            if (hasReplyStatus(record, TICKET_QUERY)) {
              return (
                <Tooltip placement="bottom" title="Reply">
                  <div className="reply-grievance text-center" onClick={(): any => reply(record)}>
                    <ReplyIcon />
                  </div></Tooltip>
              );
            }

            if (hasReplyStatus(record, REOPEN)) {
              return (
                <Tooltip placement="bottom" title="Reopen">
                  <div className="open-grievance text-center" onClick={(event: any): any => {
                    reply(record);
                  }}
                  >
                    <ReopenIcon />
                  </div></Tooltip>
              );
            }

            return null;
          },
        },
      ],
    );
  }, [appDetail]);

  const setLoaderStateValue = (): void => {
    setClaimDataCount(-1);
    setGrievanceList(null);
  };

  const getClaimsList = (data: any): void => {
    const { grievanceDetails, count, isSuccess, errors = [] } = data;
    if (isSuccess) {
      grievanceDetails.map((detail: any, index: number): void => {
        detail.key = index;
      });
      setIsGrievanceLoading(false);
      setGrievanceList(grievanceDetails);
      setClaimDataCount(count);
    } else {
      setGrievanceList([]);
      setIsGrievanceLoading(false);
      setClaimDataCount(count);
      centeredNotification(errors.join(',\n'));
    }
  };

  const handleTableChange = (...attributes: any): any => {
    const { columnKey: key, order: value } = attributes[2];
    const columnToUpdate = claimsColumns.find(({ key: colKey }: any): boolean => colKey === key);
    columnToUpdate.sortOrder = value;
    setClaimsColumns([...claimsColumns]);
    dispatch(searchClaimList());
    dispatch(searchInPatientList());
    dispatch(setSortingPreference(value ? { key, value: String(value).toUpperCase() } : null));
  };

  const reply = (selectedRecord: any): any => {
    if (selectedRecord?.hospactionId) {
      const encodedRecord = base64UrlEncoded(JSON.stringify(selectedRecord));
      history.push(`${GRIEVANCE_DETAIL_ROUTE}${encodedRecord}/reply`);
      const replyGrievanceData = {
        ticketId: selectedRecord?.ticketId,
        payerId: selectedRecord?.payerId,
        productCode: selectedRecord?.productCode,
        hospactionId: selectedRecord?.hospactionId,
      };
      dispatch(replyGrievance(replyGrievanceData, (): any => { }, onReplyError));
    }
  };

  const onReplyError = (error: any): any => {
    centeredNotification(error || COMMON_ERROR);
  };

  const rowSelect = (event: any, record: any, index: any): any => {
    if (record) {
      const encodedRecord = base64UrlEncoded(JSON.stringify(record));
      const buttonType = hasReplyStatus(record, REOPEN) ? GRIEVANCE_ACTIONS?.REOPEN :
        hasReplyStatus(record, TICKET_QUERY) ? GRIEVANCE_ACTIONS?.QUERY : undefined;
      history.push(`view/detail/${encodedRecord}?buttonType=${buttonType}`);
    }
  };

  const pageChange = (pageNumber: any): any => {
    dispatch(setPageIndex(pageNumber));
    dispatch(searchInPatientList());
  };

  const onColumnEditSave = (form: FormInstance, setIsVisible: any): any => {
    // payload, setIsVisible, isReconciliation, isClaimsDisplay, isGrievancePage, isViewGrievancePage
    dispatch(saveInPatientConfig(form.getFieldsValue(), setIsVisible, 'grievance'));
  };

  const tableTitle = (): any => {
    return (<Row className="margin-button" gutter={GUTTOR_SPACE}>
      <Col>
        <Form
          form={viewForm}>
          <TableConfigurator
            tableMapping={View_Grievance}
            saveClickHandler={onColumnEditSave}
            configuration={appDetail.getGrievanceConfig()}
            isViewGrievancePage={true}
          />
        </Form>
      </Col>
    </Row>
    );
  };

  return (
    <>
      <Form>
        <Table
          data-testid="in-patient-list"
          className="ant-tabs-content-holder grievance-tbl"
          rowKey="id"
          title={tableTitle}
          size={TABLE_SIZE_MIDDLE}
          columns={claimsColumns}
          dataSource={grievanceList}
          pagination={false}
          loading={isGrievanceLoading}
          onChange={handleTableChange}
          onRow={(record: any, rowIndex: any): any => ({
            onClick: (event: React.MouseEvent<HTMLTableRowElement>): any =>
              rowSelect(event, record, rowIndex),
          })}
          locale={{
            emptyText:
              claimDataCount !== -1 ? (
                <Empty />
              ) : (
                <div className="full-skelton-in-list">
                  {blankArr.map((_: any, index: any): any => (
                    <Skeleton key={index} active={true} paragraph={{ rows: 4 }} />
                  ))}
                </div>
              ),
          }}
        />
      </Form>
      {(claimDataCount > 0) && <Pagination
        data-testid="pagination"
        showSizeChanger={false}
        onChange={pageChange}
        defaultCurrent={1}
        total={claimDataCount}
        current={pageIndex}
        className="mt-md text-center"
      />}

    </>
  );
};

export default GrievanceList;
